import { type ClientOptions } from 'graphql-ws';

import { getSinkError, type SinkErrorKind } from '../sink';

/**
 * A connection-lifecycle event of the shared socket, normalised. `graphql-ws` types every
 * listener argument as `unknown`, so the narrowing happens here rather than in each consumer.
 *
 * Nothing is derived from a listener argument, only counted: the arguments differ between the
 * `graphql-ws` versions this package's consumers pin, while the events do not.
 *
 * Retry exhaustion has no event. The client stops emitting `connecting` and throws the last
 * close into every subscription's sink instead, so it shows up here as an `attempt` count that
 * climbs with no `connected` after it.
 */
export type WsConnectionEvent =
	/** `attempt` is 1 for a first try, higher for a retry. */
	| { type: 'connecting'; attempt: number }
	/** Socket open, `connection_init` not acknowledged yet. */
	| { type: 'opened'; attempt: number }
	/** `connectionCount` is 1 for the page's first connection, above 1 for a re-open. */
	| { type: 'connected'; attempt: number; connectionCount: number }
	/** Close fields are null when the close event did not carry them. */
	| {
			type: 'closed';
			closeCode: number | null;
			reason: string | null;
			wasClean: boolean | null;
			connectedForMs: number | null;
	  }
	/** `message` is set only for a real `Error`; a browser socket error carries no detail. */
	| { type: 'error'; message: string | null; kind: SinkErrorKind };

// Read defensively: a browser close is a `CloseEvent`, but `Client.terminate()` emits a plain
// `{ code, reason, wasClean }`.
const readCloseInfo = (
	event: unknown,
): { closeCode: number | null; reason: string | null; wasClean: boolean | null } => {
	if (typeof event !== 'object' || event === null) {
		return { closeCode: null, reason: null, wasClean: null };
	}
	const code = 'code' in event ? event.code : undefined;
	const reason = 'reason' in event ? event.reason : undefined;
	const wasClean = 'wasClean' in event ? event.wasClean : undefined;
	return {
		closeCode: typeof code === 'number' ? code : null,
		reason: typeof reason === 'string' ? reason : null,
		wasClean: typeof wasClean === 'boolean' ? wasClean : null,
	};
};

/**
 * Reporting, the pong deadline and the connection flag are built together because all three are
 * driven by the same events and all three act on `closed`. A zero `pongWaitTimeout` disables the
 * deadline.
 */
export const createSocketListeners = ({
	onConnectionEvent,
	pongWaitTimeout,
	terminate,
}: {
	onConnectionEvent?: (event: WsConnectionEvent) => void;
	pongWaitTimeout: number;
	terminate: () => void;
}): { listeners: ClientOptions['on']; isConnected: () => boolean } => {
	// Per client, so they are dropped along with it.
	let attempt = 0;
	let connectionCount = 0;
	let connectedAt: number | null = null;
	let pongDeadline: ReturnType<typeof setTimeout> | undefined;

	// These run inside the socket's own event handlers, where a throw would break the transport.
	const report = (event: WsConnectionEvent): void => {
		if (!onConnectionEvent) {
			return;
		}
		try {
			onConnectionEvent(event);
		} catch {
			// Nowhere left to report a failure of the reporting itself.
		}
	};

	const clearPongDeadline = (): void => {
		clearTimeout(pongDeadline);
		pongDeadline = undefined;
	};

	return {
		// `connectedAt` doubles as the connection flag: set on connect, cleared on close.
		isConnected: () => connectedAt !== null,
		listeners: {
			connecting: () => {
				attempt += 1;
				report({ type: 'connecting', attempt });
			},
			opened: () => {
				report({ type: 'opened', attempt });
			},
			connected: () => {
				connectionCount += 1;
				connectedAt = Date.now();
				report({ type: 'connected', attempt, connectionCount });
				attempt = 0;
			},
			closed: (event) => {
				// A deadline must not outlive its socket. `terminate()` acts on whatever connection
				// exists when it runs, and a retry can be in flight within milliseconds.
				clearPongDeadline();
				report({
					type: 'closed',
					...readCloseInfo(event),
					connectedForMs: connectedAt === null ? null : Date.now() - connectedAt,
				});
				connectedAt = null;
			},
			// Only a ping this client sent arms the deadline, and only a pong from the server
			// clears it. `graphql-ws` emits both events in each direction, and the frames it did
			// not originate say nothing about whether ours are landing.
			ping: (received) => {
				if (received || pongWaitTimeout <= 0) {
					return;
				}
				clearPongDeadline();
				pongDeadline = setTimeout(terminate, pongWaitTimeout);
			},
			pong: (received) => {
				if (received) {
					clearPongDeadline();
				}
			},
			error: (error) => {
				// Only the classification comes from `getSinkError`, so it agrees with the sink
				// path. `getSinkError` renders a placeholder message for the detail-free socket
				// events, and a placeholder in an analytics attribute reads as real information.
				report({
					type: 'error',
					message: error instanceof Error ? error.message : null,
					kind: getSinkError(error).kind,
				});
			},
		},
	};
};
